"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { ShoppingCart, Plus, Minus, X, Bookmark } from "lucide-react";
import { useCart } from "@/lib/stores/cartStore";
import { cn } from "@/lib/utils";
import { DeleteButton } from "@/components/ui/DeleteButton";



interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartSidebar({ isOpen, onClose }: CartSidebarProps) {
  const {
    cart,
    updateItemQuantity,
    removeItem,
    isEmpty,
    getItemsBySeller
  } = useCart();

  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [renderKey, setRenderKey] = useState(0);
  const [newlyAddedItemId, setNewlyAddedItemId] = useState<string | null>(null);


  // Force re-render when cart changes
  useEffect(() => {
    console.log('🛒 CartSidebar re-rendering with cart items:', cart.items.length);
    setRenderKey(prev => prev + 1);
  }, [cart.items.length, cart.totalItems, cart.subtotal]);

  // Listen for newly added items and animate them
  useEffect(() => {
    const handleNewItemAdded = (event: CustomEvent) => {
      console.log('🆕 CartSidebar received cart-item-highlight event:', event.detail);
      const itemId = event.detail?.itemId;
      if (itemId) {
        console.log('🆕 New item added to cart:', itemId);
        setNewlyAddedItemId(itemId);

        // Clear the highlight after animation
        setTimeout(() => {
          setNewlyAddedItemId(null);
        }, 3000);

        // Scroll to the newly added item
        setTimeout(() => {
          const newItemElement = document.getElementById(`cart-item-${itemId}`);
          if (newItemElement) {
            console.log('📜 Scrolling to newly added item:', itemId);
            newItemElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    };

    console.log('🎧 CartSidebar listening for cart-item-highlight events');
    window.addEventListener('cart-item-highlight', handleNewItemAdded as EventListener);

    return () => {
      console.log('🔇 CartSidebar removing event listener');
      window.removeEventListener('cart-item-highlight', handleNewItemAdded as EventListener);
    };
  }, []);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Only prevent scrolling on main content, not entire body
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore scrolling on main content
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.style.overflow = 'unset';
      }
    };
  }, [isOpen, onClose]);

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    setIsUpdating(itemId);
    try {
      updateItemQuantity(itemId, newQuantity);
    } finally {
      setIsUpdating(null);
    }
  };







  if (!isOpen) return null;

  const subtotal = cart.subtotal;
  const shipping = subtotal > 50 ? 0 : 5.99;
  const tax = subtotal * 0.23;
  const total = subtotal + shipping + tax;

  return (
    <>
      {/* Sidebar */}
      <div className={cn(
        "fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Shopping Cart ({cart.totalItems})
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto">
            {isEmpty() ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <ShoppingCart className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium mb-2">Your cart is empty</h3>
                <p className="text-gray-600 mb-4">
                  Add some products to get started
                </p>
                <Link href="/search" onClick={onClose}>
                  <Button>Continue Shopping</Button>
                </Link>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {/* Items grouped by seller */}
                {Object.entries(getItemsBySeller()).map(([sellerId, items]) => (
                  <Card key={sellerId} className="overflow-hidden">
                    <CardContent className="p-0">
                      {/* Seller Header */}
                      <div className="bg-gray-50 px-4 py-3 border-b">
                        <Link href={`/stall/${items[0]?.sellerId || ''}`}>
                          <h3 className="font-medium text-sm hover:text-blue-600 transition-colors cursor-pointer">
                            Sold by: {items[0]?.sellerName || 'Unknown Seller'}
                          </h3>
                        </Link>
                        <p className="text-xs text-gray-600">
                          {items.length} item{items.length !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Items */}
                      <div className="divide-y">
                        {items.map((item) => (
                          <div
                            key={item.id}
                            id={`cart-item-${item.id}`}
                            className={cn(
                              "p-4 transition-all duration-500",
                              newlyAddedItemId === item.id && "animate-pulse bg-green-50 border-l-4 border-green-400"
                            )}
                          >
                            <div className="flex gap-3">
                              {/* Product Image */}
                              <div className="flex-shrink-0">
                                <Link href={`/product/${item.productId}`}>
                                  <img
                                    src={item.productImage}
                                    alt={item.productTitle}
                                    className="w-16 h-16 object-cover rounded-lg border hover:opacity-80 transition-opacity cursor-pointer"
                                  />
                                </Link>
                              </div>

                              {/* Product Details */}
                              <div className="flex-1 min-w-0">
                                <Link href={`/product/${item.productId}`}>
                                  <h4 className="font-medium text-sm mb-1 line-clamp-2 hover:text-blue-600 transition-colors cursor-pointer">
                                    {item.productTitle}
                                  </h4>
                                </Link>
                                <p className="text-xs text-gray-600 mb-2">
                                  Condition: {item.condition}
                                </p>
                                <p className="text-sm font-bold text-green-600">
                                  €{item.price.toFixed(2)}
                                </p>
                              </div>

                              {/* Quantity and Actions */}
                              <div className="flex flex-col items-end gap-2">
                                {/* Quantity Controls */}
                                <div className="flex items-center border rounded">
                                  <button
                                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                    disabled={isUpdating === item.id || item.quantity <= 1}
                                    className="p-1 hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="px-2 py-1 text-xs min-w-[2rem] text-center">
                                    {isUpdating === item.id ? '...' : item.quantity}
                                  </span>
                                  <button
                                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                    disabled={isUpdating === item.id}
                                    className="p-1 hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>

                                {/* Item Total */}
                                <p className="text-xs font-semibold">
                                  €{(item.price * item.quantity).toFixed(2)}
                                </p>

                                <DeleteButton
                                  itemId={item.id}
                                  itemTitle={item.productTitle}
                                  productId={item.productId}
                                  onRemoveItem={removeItem}
                                  size="sm"
                                  onSaveForLater={(itemId, itemTitle) => {
                                    console.log(`Saved ${itemTitle} for later`);
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Footer with totals and actions */}
          {!isEmpty() && (
            <div className="border-t bg-gray-50 p-4 space-y-3">
              {/* Order Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal ({cart.totalItems} items)</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? "text-green-600" : ""}>
                    {shipping === 0 ? "Free" : `€${shipping.toFixed(2)}`}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Tax (23% VAT)</span>
                  <span>€{tax.toFixed(2)}</span>
                </div>

                <hr className="my-2" />

                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <Link href="/cart" onClick={onClose} className="block">
                  <Button className="w-full" size="lg">
                    View Full Cart
                  </Button>
                </Link>

                <Link href="/save-for-later" onClick={onClose} className="block">
                  <Button variant="outline" className="w-full" size="sm">
                    <Bookmark className="h-4 w-4 mr-2" />
                    View Saved Items
                  </Button>
                </Link>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    console.log('🛒 Proceeding to checkout from sidebar...');
                    onClose();
                    // Navigate to checkout page
                    window.location.href = '/checkout';
                  }}
                >
                  Proceed to Checkout
                </Button>
              </div>

              {/* Shipping note */}
              {subtotal < 50 && (
                <p className="text-xs text-gray-600 text-center">
                  Add €{(50 - subtotal).toFixed(2)} more for free shipping!
                </p>
              )}
            </div>
          )}
        </div>
      </div>


    </>
  );
}
